// Roleplay + library + session-save calls (split from trainingApiClient for file-size hygiene).
import { ADMIN_ACTION, trainingFetch, type FetchResult } from "@/lib/training/trainingApiClientCore";

export type RoleplayLine = { role: "salesperson" | "customer"; content: string };

export type RoleplayScore = {
  overallScore: number;
  clarity: number;
  accuracy: number;
  objectionHandling: number;
  answersCustomerQuestions?: number;
  usesFollowUpQuestions?: number;
  usesOpenEndedQuestions?: number;
  /** Legacy sessions only */
  empathy?: number;
  summary: string;
  strengths: string[];
  improvements: string[];
};

export type RoleplayScenarioListItem = {
  key: string;
  label: string;
  persona: string;
  source: "org" | "manufacturer" | "platform_example";
  id?: string;
  kb_context_digest?: string | null;
};

export type RoleplayOrgScenarioRow = {
  id: string;
  scenario_key: string;
  label: string;
  persona: string;
  is_active: boolean;
  is_sharable: boolean;
  sort_order: number;
  kb_context_digest: string | null;
  trainer_rubric_markdown: string | null;
  example_questions: unknown;
  ideal_answer_outline: string | null;
};

export type ArchivedRoleplaySession = {
  id: string;
  user_id: string;
  traineeLabel?: string;
  scenario_key: string;
  overall_score: number | null;
  summary: string | null;
  created_at: string;
  reviewed_at?: string | null;
  archived_at?: string | null;
  review_status?: string;
  message_count?: number;
};

export async function fetchRoleplayScenarioList(
  organizationId: string,
  opts?: { orgEditor?: boolean; includePlatformExample?: boolean }
): Promise<
  FetchResult<
    | { ok: true; scenarios: RoleplayScenarioListItem[]; orgScenarios?: RoleplayOrgScenarioRow[] }
    | { ok?: false; error?: string }
  >
> {
  const sp = new URLSearchParams();
  sp.set("action", "training-roleplay-library");
  sp.set("organizationId", organizationId);
  if (opts?.orgEditor) sp.set("orgEditor", "1");
  if (opts?.includePlatformExample) sp.set("includePlatformExample", "1");
  return trainingFetch(`${ADMIN_ACTION}?${sp}`, { method: "GET" });
}

export async function postRoleplayLibrary(
  organizationId: string,
  body: Record<string, unknown>
): Promise<FetchResult<{ ok?: boolean; error?: string; id?: string; drafts?: unknown[]; suggestion?: unknown }>> {
  const sp = new URLSearchParams();
  sp.set("action", "training-roleplay-library");
  return trainingFetch(`${ADMIN_ACTION}?${sp}`, {
    method: "POST",
    body: JSON.stringify({ ...body, organizationId }),
  });
}

export async function fetchArchivedTrainingSessions(organizationId: string): Promise<
  FetchResult<{ ok: true; archivedSessions: ArchivedRoleplaySession[] } | { ok?: false; error?: string }>
> {
  const sp = new URLSearchParams();
  sp.set("action", "training-session");
  sp.set("organizationId", organizationId);
  sp.set("archivedQueue", "1");
  return trainingFetch(`${ADMIN_ACTION}?${sp}`, { method: "GET" });
}

export async function fetchRoleplayOpening(params: {
  organizationId: string;
  locationId?: string | null;
  scenarioKey: string;
}): Promise<
  FetchResult<{ ok: true; scenarioKey: string; scenarioLabel: string; customerLine: string } | { ok?: false; error?: string }>
> {
  const sp = new URLSearchParams();
  sp.set("action", "training-roleplay");
  return trainingFetch(`${ADMIN_ACTION}?${sp}`, {
    method: "POST",
    body: JSON.stringify({
      action: "turn",
      organizationId: params.organizationId,
      locationId: params.locationId ?? null,
      scenarioKey: params.scenarioKey,
      messages: [],
    }),
  });
}

export async function fetchRoleplayTurn(params: {
  organizationId: string;
  locationId?: string | null;
  scenarioKey: string;
  messages: RoleplayLine[];
  salespersonLine: string;
}): Promise<
  FetchResult<{ ok: true; scenarioKey: string; scenarioLabel: string; customerLine: string } | { ok?: false; error?: string }>
> {
  const sp = new URLSearchParams();
  sp.set("action", "training-roleplay");
  return trainingFetch(`${ADMIN_ACTION}?${sp}`, {
    method: "POST",
    body: JSON.stringify({
      action: "turn",
      organizationId: params.organizationId,
      locationId: params.locationId ?? null,
      scenarioKey: params.scenarioKey,
      messages: params.messages,
      salespersonLine: params.salespersonLine,
    }),
  });
}

export async function fetchRoleplayScore(params: {
  organizationId: string;
  locationId?: string | null;
  scenarioKey: string;
  messages: RoleplayLine[];
}): Promise<FetchResult<{ ok: true; scenarioKey: string; score: RoleplayScore } | { ok?: false; error?: string }>> {
  const sp = new URLSearchParams();
  sp.set("action", "training-roleplay");
  return trainingFetch(`${ADMIN_ACTION}?${sp}`, {
    method: "POST",
    body: JSON.stringify({
      action: "score",
      organizationId: params.organizationId,
      locationId: params.locationId ?? null,
      scenarioKey: params.scenarioKey,
      messages: params.messages,
    }),
  });
}

export async function saveTrainingSession(body: {
  organizationId: string;
  scenarioKey: string;
  locationKey?: string | null;
  messages: RoleplayLine[];
  score: RoleplayScore | null;
  summary?: string | null;
}): Promise<FetchResult<{ ok: true; id: string; created_at: string }>> {
  const sp = new URLSearchParams();
  sp.set("action", "training-session");
  return trainingFetch(`${ADMIN_ACTION}?${sp}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
